import React from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import * as Routes from '@/modules/routes';
import cn from '@/utils/cn';
import SPAButton from '../../shared/SPAButton';
import SPAPill from '../../shared/SPAPill';
import { filteredTransactionsPath, formatInvoicedOn } from './TransactionResults';
import { isSearchable } from './hooks';
import { formatDate } from './format';
import rolePillTone from './rolePillTone';
import type {
  SearchField, TransactionRow, UserSearchResponse, UserSearchRow,
} from './types';

export const selectionKey = (row: Pick<UserSearchRow, 'id' | 'user_type'>) => `${row.user_type}:${row.id}`;

const GroupLabel = ({ count, label }: { count: number; label: string }) => (
  <h2
    className={cn(
      'tw-mb-1 tw-mt-5 tw-flex tw-items-baseline tw-gap-2 tw-px-3',
      'tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-spa-secondary',
    )}
  >
    {label}
    <span className="tw-font-normal tw-normal-case tw-tracking-normal tw-text-spa-tertiary">{count}</span>
  </h2>
);

// Selected rows get an accent border on the faint accent wash rather than the rail's accent fill or
// the raised fill: the pills keep their tints, and the muted text keeps 4.5:1 (it doesn't on
// bg-tertiary in dark).
const rowClass = (selected: boolean) => cn(
  'tw-mt-1 tw-box-border tw-flex tw-w-full tw-flex-col tw-gap-0.5 tw-rounded-md tw-border tw-border-solid',
  'tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-text-spa-primary tw-cursor-pointer tw-transition-colors',
  selected
    ? 'tw-border-spa-accent tw-bg-spa-accent-bg-subtle'
    : 'tw-border-transparent tw-bg-transparent hover:tw-bg-spa-bg-tertiary',
);

const metaClass = 'tw-block tw-truncate tw-text-xs tw-text-spa-secondary';

interface ResultRowButtonProps {
  row: UserSearchRow;
  selected: boolean;
  showExactPill: boolean;
  onSelect: (row: UserSearchRow) => void;
}

const ResultRowButton = ({
  row, selected, showExactPill, onSelect,
}: ResultRowButtonProps) => (
  <button
    aria-current={selected ? 'true' : undefined}
    className={rowClass(selected)}
    type="button"
    onClick={() => onSelect(row)}
  >
    <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
      <span className="tw-font-semibold tw-text-spa-heading">
        {row.first_name} {row.last_name}
      </span>
      <SPAPill className="tw-capitalize" tone={rolePillTone(row.role)}>{row.role}</SPAPill>
      {row.exact && showExactPill && <SPAPill tone="accent">Exact</SPAPill>}
      {!row.active && <SPAPill>Inactive</SPAPill>}
      {row.locked && <SPAPill tone="error">Locked</SPAPill>}
    </span>
    <span className="tw-block tw-truncate tw-text-xs tw-text-spa-secondary">
      {row.email || row.username}
      {row.email && row.username && ` · ${row.username}`}
    </span>
    {row.subscriber && (
      <span className={metaClass}>
        {row.subscriber.name}
        {row.subscriber.customer_number && ` · Customer #${row.subscriber.customer_number}`}
      </span>
    )}
    <span className={metaClass}>
      Last sign-in {formatDate(row.current_sign_in_at)}
    </span>
  </button>
);

const TransactionListRow = ({ row }: { row: TransactionRow }) => (
  <a
    className={cn(rowClass(false), 'tw-flex-row tw-items-center tw-gap-3 tw-no-underline hover:tw-no-underline')}
    href={row.subscriber
      ? filteredTransactionsPath(row.subscriber.id, row.number)
      : Routes.search_admin_search_purchase_orders_path()}
  >
    <span className="tw-min-w-0 tw-flex-1">
      <span className="tw-block tw-font-semibold tw-text-spa-accent-text">{row.number}</span>
      <span className="tw-block tw-truncate tw-text-xs tw-text-spa-tertiary">
        {row.subscriber?.name ?? row.admin_email} · Invoiced {formatInvoicedOn(row.invoiced_on)}
      </span>
    </span>
    <SPAPill>
      {row.subscriptions_count} txn{row.subscriptions_count === 1 ? '' : 's'}
    </SPAPill>
  </a>
);

const ResultsSkeleton = () => (
  <div aria-hidden="true" className="tw-mt-6 tw-flex tw-flex-col tw-gap-4 tw-px-3" data-testid="results-skeleton">
    <div className="tw-h-3 tw-w-20 tw-animate-pulse tw-rounded tw-bg-spa-bg-skeleton" />
    {[0, 1, 2].map(row => (
      <div key={row} className="tw-flex tw-flex-col tw-gap-2">
        <div className="tw-h-3.5 tw-w-1/2 tw-animate-pulse tw-rounded tw-bg-spa-bg-skeleton" />
        <div className="tw-h-3 tw-w-3/4 tw-animate-pulse tw-rounded tw-bg-spa-bg-skeleton-subtle" />
        <div className="tw-h-3 tw-w-2/3 tw-animate-pulse tw-rounded tw-bg-spa-bg-skeleton-subtle" />
      </div>
    ))}
  </div>
);

const noteClass = 'tw-mb-0 tw-mt-5 tw-px-3 tw-text-sm tw-text-spa-secondary';

interface UserResultsListProps {
  exact: boolean;
  field: SearchField;
  maxResults: boolean;
  q: string;
  search: UseQueryResult<UserSearchResponse>;
  searching: boolean;
  selectedKey: string | null;
  onSelect: (row: UserSearchRow) => void;
  onShowAll: () => void;
}

const UserResultsList = ({
  exact, field, maxResults, q, search, searching, selectedKey, onSelect, onShowAll,
}: UserResultsListProps) => {
  if (!isSearchable(q, field)) {
    return (
      <p className={noteClass}>
        {field === 'id'
          ? 'Enter a numeric user ID to search.'
          : 'Enter a search to get started.'}
      </p>
    );
  }

  if (searching) {
    return <ResultsSkeleton />;
  }

  if (search.isError) {
    return (
      <p className={cn(noteClass, 'tw-text-spa-error')}>
        Something went wrong running the search. Try again.
      </p>
    );
  }

  const { data: rows, meta, transactions } = search.data;
  const anyTruncated = meta.staffers_truncated || meta.students_truncated || meta.transactions_truncated;

  return (
    <div>
      {rows.length === 0 ? (
        <div className="tw-mt-5 tw-px-3 tw-text-sm tw-text-spa-secondary">
          <p className="tw-m-0">No users matched your search.</p>
          <p className="tw-mb-0 tw-mt-1">
            {exact
              ? 'Exact match is on, so only complete matches are found — uncheck it to match partial values.'
              : 'Matches start at the beginning of a word — try the start of a name, email, or username, '
                + 'or search by a different field.'}
          </p>
        </div>
      ) : (
        <>
          <GroupLabel count={rows.length} label="Users" />
          {rows.map(row => (
            <ResultRowButton
              key={selectionKey(row)}
              row={row}
              selected={selectedKey === selectionKey(row)}
              showExactPill={!exact}
              onSelect={onSelect}
            />
          ))}
        </>
      )}

      {field === 'email' && (
        <>
          <GroupLabel count={transactions.length} label="Transactions" />
          {transactions.length === 0 ? (
            <p className="tw-mb-0 tw-mt-1 tw-px-3 tw-text-sm tw-text-spa-secondary">
              No orders have this contact email.
            </p>
          ) : (
            transactions.map(row => <TransactionListRow key={row.id} row={row} />)
          )}
        </>
      )}

      {anyTruncated && (maxResults ? (
        <p className={cn(noteClass, 'tw-mt-4')}>
          There are more matches than shown — refine your search to narrow it down.
        </p>
      ) : (
        <div className="tw-mt-4 tw-px-3">
          {search.isFetching ? (
            <p className="tw-m-0 tw-text-sm tw-text-spa-secondary">Loading more matches…</p>
          ) : (
            <SPAButton size="xs" onClick={onShowAll}>Show more matches</SPAButton>
          )}
        </div>
      ))}
    </div>
  );
};

export default UserResultsList;
